import { useState, useMemo, Fragment, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  RefreshCw,
  Loader2,
  ClipboardCheck,
  CheckCheck,
  Play,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  Layers,
  Send,
  Eye,
  EyeOff,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  FileDiff,
  Copy,
  LayoutGrid,
  List,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  useReviewQueueGrouped,
  useReviewQueueCount,
  useMarkProductInProgress,
  useMarkVariantInProgress,
} from "@/hooks/useReview";
import { useScrapersSources } from "@/hooks/useScrapersSources";
import { useIssuesCount } from "@/hooks/useValidation";
import { useAuth } from "@/hooks/useAuth";
import { ReviewItemDialog } from "@/components/review/ReviewItemDialog";
import { BulkReviewDialog } from "@/components/review/BulkReviewDialog";
import { ResolveIssueDialog } from "@/components/review/ResolveIssueDialog";
import { ReviewMatrix } from "@/components/review/ReviewMatrix";
import { IssuesBulkResolveDialog } from "@/components/validation/IssuesBulkResolveDialog";
import { SourceCombobox } from "@/components/review/SourceCombobox";
import * as reviewApi from "@/services/reviewApi";
import {
  getBatchedAgentPrompt,
  getAgentPrompt,
  getProductIssues,
  getVariantIssues,
} from "@/services/validationApi";
import { scrapeProductById } from "@/services/scrapersApi";
import { formatRelativeTime } from "@/lib/utils";
import type {
  ReviewQueueItem,
  ReviewQueueParams,
  ReviewStatus,
} from "@/types/review";
import type { ValidationIssue } from "@/types/validation";
import { Toaster, toast } from "sonner";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { useUrlState } from "@/hooks/useUrlState";
import { useSessionState } from "@/hooks/useSessionState";
import { serializeSet, deserializeSet } from "@/lib/stateUtils";
import type { ReviewUrlState, ReviewSessionState } from "@/types/pageState";

const QUEUE_PAGE_SIZE = 20;

/** Grade (or consecutive) run within a product when reviewing variants. */
type ReviewQueueSubgroup = {
  key: string;
  label: string;
  items: ReviewQueueItem[];
};

type ReviewQueueTableGroup = {
  key: string;
  label: string;
  items: ReviewQueueItem[];
  /** Variants-only: consecutive runs by grade, same rules as the default queue grouping. */
  subgroups?: ReviewQueueSubgroup[];
};

/**
 * Split variant rows in queue order into consecutive groups that share the same grade_name
 * (mirrors the non–variants-only variant grouping logic).
 */
function splitConsecutiveGradeRuns(
  items: ReviewQueueItem[],
): { label: string; items: ReviewQueueItem[] }[] {
  const result: { label: string; items: ReviewQueueItem[] }[] = [];
  let current: ReviewQueueItem[] = [];
  let currentGrade: string | null = null;

  const flush = () => {
    if (current.length === 0) return;
    const first = current[0]!;
    const gradeLabel =
      first.grade_name != null && first.grade_name !== ""
        ? first.grade_name
        : null;
    const kind = first.variant_name ? "variant" : "grade";
    const label = [
      gradeLabel,
      `${current.length} ${kind}${current.length !== 1 ? "s" : ""}`,
    ]
      .filter(Boolean)
      .join(" · ");
    result.push({ label, items: [...current] });
  };

  for (const item of items) {
    const g = item.grade_name ?? "";
    if (current.length > 0 && (currentGrade ?? "") !== g) {
      flush();
      current = [];
      currentGrade = null;
    }
    current.push(item);
    currentGrade = g;
  }
  flush();
  return result;
}

function variantSubgroupsNeedChrome(subgroups: ReviewQueueSubgroup[]): boolean {
  if (subgroups.length === 0) return false;
  if (subgroups.length > 1) return true;
  const sg = subgroups[0]!;
  if (sg.items.length > 1) return true;
  const g = sg.items[0]?.grade_name;
  return g != null && g !== "";
}

type ReviewStatusFilterOption = ReviewStatus | "has_issues";

const REVIEW_STATUS_OPTIONS: {
  value: ReviewStatusFilterOption;
  label: string;
}[] = [
  { value: "unreviewed", label: "Unreviewed" },
  { value: "stale", label: "Stale" },
  { value: "in_progress", label: "In progress" },
  { value: "reviewed", label: "Reviewed" },
  { value: "has_issues", label: "Has issues" },
  { value: "canceled", label: "Canceled" },
];

function StatusBadge({ status }: { status: ReviewStatus }) {
  const colorClass =
    status === "reviewed"
      ? "bg-[#10B981] border-[#10B981] text-white"
      : status === "stale"
        ? "bg-[#F59E0B] border-[#F59E0B] text-white"
        : status === "in_progress"
          ? "bg-[#3B82F6] border-[#3B82F6] text-white"
          : status === "canceled"
            ? "bg-[#94A3B8] border-[#94A3B8] text-white"
            : "bg-[#6B7280] border-[#6B7280] text-white";
  const label = status === "in_progress" ? "in progress" : status;
  return (
    <Badge variant="outline" className={cn("uppercase text-xs", colorClass)}>
      {label}
    </Badge>
  );
}

export default function Review() {
  const { user } = useAuth();
  const reviewerId = user?.email ?? user?.name ?? "unknown";

  // URL state for filters and pagination
  const [urlState, setUrlState] = useUrlState<ReviewUrlState>({
    schema: {
      status: "string",
      source: "string",
      entity_type: "string",
      product_id: "number",
      page: "number",
      hideReviewed: "boolean",
      hideCanceled: "boolean",
      hideReviewedWithoutIssues: "boolean",
      view_mode: "string",
    },
    defaults: { page: 1 },
    replace: true,
  });

  // Session state for dialogs, expanded groups, and selected items for batch submit
  const [sessionState, setSessionState] = useSessionState<ReviewSessionState>({
    key: "review-ui",
    defaultValue: {
      reviewDialogOpen: false,
      bulkDialogOpen: false,
      collapsedReviewGroups: new Set<string>(),
      selectedReviewItems: new Set<string>(),
      expandedReviewRowKey: null,
    },
    serialize: (state) =>
      JSON.stringify({
        ...state,
        collapsedReviewGroups: serializeSet(state.collapsedReviewGroups),
        selectedReviewItems: serializeSet(state.selectedReviewItems),
      }),
    deserialize: (str) => {
      const parsed = JSON.parse(str);
      return {
        ...parsed,
        collapsedReviewGroups: deserializeSet(parsed.collapsedReviewGroups),
        selectedReviewItems: deserializeSet(parsed.selectedReviewItems ?? []),
        expandedReviewRowKey: parsed.expandedReviewRowKey ?? null,
      };
    },
  });

  // Transient state (not persisted)
  const [selectedItem, setSelectedItem] = useState<ReviewQueueItem | null>(
    null,
  );
  const [resolveIssue, setResolveIssue] = useState<ValidationIssue | null>(
    null,
  );
  const [bulkResolveIssues, setBulkResolveIssues] = useState<
    ValidationIssue[] | null
  >(null);
  const [selectedValidationIssueIds, setSelectedValidationIssueIds] = useState<
    Set<number>
  >(new Set());
  const [promptLoadingKey, setPromptLoadingKey] = useState<string | null>(null);
  const [markingPage, setMarkingPage] = useState(false);
  const [startingKey, setStartingKey] = useState<string | null>(null);
  const [scrapingKey, setScrapingKey] = useState<string | null>(null);
  const [scrapeProgress, setScrapeProgress] = useState<
    "queued" | "in_progress" | null
  >(null);
  const [submittingSelected, setSubmittingSelected] = useState(false);
  const [batchPromptLoading, setBatchPromptLoading] = useState(false);

  // Parse selected statuses from URL state (comma-separated)
  const selectedStatuses = useMemo(() => {
    const s = urlState.status?.trim();
    if (!s) return new Set<ReviewStatusFilterOption>();
    return new Set(
      s
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean) as ReviewStatusFilterOption[],
    );
  }, [urlState.status]);

  // Derive API params from URL state
  const params = useMemo<ReviewQueueParams>(() => {
    const p: ReviewQueueParams = {
      limit: QUEUE_PAGE_SIZE,
      offset: ((urlState.page || 1) - 1) * QUEUE_PAGE_SIZE,
    };
    const rawStatusList = Array.from(selectedStatuses);
    const hasIssuesFilter = rawStatusList.includes("has_issues");

    // Strip the virtual "has_issues" option — it is not a real API status.
    const apiStatuses = rawStatusList.filter(
      (s): s is ReviewStatus => s !== "has_issues",
    );

    // Count only real (non-virtual) status options to determine "all selected".
    const realStatusOptionsCount = REVIEW_STATUS_OPTIONS.filter(
      (o) => o.value !== "has_issues",
    ).length;
    if (apiStatuses.length > 0 && apiStatuses.length < realStatusOptionsCount) {
      p.status = apiStatuses.length === 1 ? apiStatuses[0]! : apiStatuses;
    }

    if (urlState.source) p.source = urlState.source;
    if (urlState.entity_type)
      p.entity_type = urlState.entity_type as "product" | "variant";
    if (urlState.product_id) p.product_id = urlState.product_id;

    // "Has issues" filter restricts results to items with open validation issues.
    if (hasIssuesFilter) p.has_open_issues = true;

    return p;
  }, [urlState, selectedStatuses]);

  const queryClient = useQueryClient();
  const { data: sources = [], isLoading: sourcesLoading } =
    useScrapersSources();
  const {
    data: queue = [],
    isLoading: queueLoading,
    refetch: refetchQueue,
  } = useReviewQueueGrouped(params);
  const {
    data: counts,
    isLoading: countsLoading,
    refetch: refetchCounts,
  } = useReviewQueueCount(params.source);
  const markProductInProgress = useMarkProductInProgress();
  const markVariantInProgress = useMarkVariantInProgress();
  const { data: issueCount, isLoading: issueCountLoading } = useIssuesCount({
    ...(urlState.source ? { source: urlState.source } : {}),
    status: "unresolved",
  });

  const handleFiltersChange = (updates: Partial<ReviewUrlState>) => {
    setUrlState((prev) => ({ ...prev, ...updates, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setUrlState((prev) => ({ ...prev, page }));
  };

  const handleRefresh = () => {
    refetchQueue();
    refetchCounts();
  };

  const handleReview = (item: ReviewQueueItem) => {
    setSelectedItem(item);
    setSessionState({ ...sessionState, reviewDialogOpen: true });
  };

  const itemKey = (item: ReviewQueueItem) =>
    `${item.entity_type}-${item.product_id}-${item.variant_id ?? 0}`;

  /** Group key for UI/session (source + product_id + grade + variant vs grade). Used for collapse state and group identity. */
  const reviewGroupKey = (item: ReviewQueueItem) =>
    [
      item.source,
      item.product_id,
      item.grade_name ?? "",
      item.variant_name ? "variant" : "grade",
    ].join("|");

  /** When viewing variants only: one UI group per product (all grades/variants together). */
  const variantQueueByProduct = urlState.entity_type === "variant";

  /** Current view mode: "variants" (default) or "matrix". */
  const viewMode = urlState.view_mode === "matrix" ? "matrix" : "variants";

  /** Queue with canceled items excluded when hideCanceled is on. */
  const queueForDisplay = useMemo(
    () =>
      urlState.hideCanceled
        ? queue.filter((item) => item.review_status !== "canceled")
        : queue,
    [queue, urlState.hideCanceled],
  );

  /**
   * Build groups from API-ordered list.
   * - Default: product = single-item group; consecutive variants with same (product_id, grade_name) = one group.
   * - Variants-only view: all variants for a product (any grade) in one group, ordered by first appearance in the queue.
   */
  const groups = useMemo((): ReviewQueueTableGroup[] => {
    const result: ReviewQueueTableGroup[] = [];

    if (variantQueueByProduct) {
      const order: string[] = [];
      const buckets = new Map<string, ReviewQueueItem[]>();
      for (const item of queueForDisplay) {
        if (item.entity_type !== "variant") continue;
        const key = `${item.source}|${item.product_id}`;
        let bucket = buckets.get(key);
        if (!bucket) {
          bucket = [];
          buckets.set(key, bucket);
          order.push(key);
        }
        bucket.push(item);
      }
      for (const key of order) {
        const items = buckets.get(key)!;
        const first = items[0]!;
        const label = [
          first.source,
          `Product ${first.product_id}`,
          first.product_name,
          `${items.length} variant${items.length !== 1 ? "s" : ""}`,
        ]
          .filter(Boolean)
          .join(" · ");
        const runs = splitConsecutiveGradeRuns(items);
        const subgroups: ReviewQueueSubgroup[] = runs.map((run, idx) => ({
          key: `${key}::sg::${idx}`,
          label: run.label,
          items: run.items,
        }));
        result.push({ key, items: [...items], label, subgroups });
      }
      return result;
    }

    let current: ReviewQueueItem[] = [];
    let currentProductId: number | null = null;
    let currentGradeName: string | null = null;

    const flush = (items: ReviewQueueItem[]) => {
      if (items.length === 0) return;
      const first = items[0]!;
      const key = reviewGroupKey(first);
      const gradeLabel =
        first.grade_name != null && first.grade_name !== ""
          ? first.grade_name
          : null;
      const kind = first.variant_name ? "variant" : "grade";
      const label = [
        first.source,
        `Product ${first.product_id}`,
        gradeLabel,
        `${items.length} ${kind}${items.length !== 1 ? "s" : ""}`,
      ]
        .filter(Boolean)
        .join(" · ");
      result.push({ key, items: [...items], label });
    };

    for (const item of queueForDisplay) {
      if (item.entity_type === "product") {
        flush(current);
        current = [];
        currentProductId = null;
        currentGradeName = null;
        flush([item]);
      } else {
        const sameGroup =
          current.length > 0 &&
          currentProductId === item.product_id &&
          (currentGradeName ?? "") === (item.grade_name ?? "");
        if (!sameGroup && current.length > 0) {
          flush(current);
          current = [];
        }
        current.push(item);
        currentProductId = item.product_id;
        currentGradeName = item.grade_name ?? null;
      }
    }
    flush(current);
    return result;
  }, [queueForDisplay, variantQueueByProduct]);

  /** When hideReviewed is on: exclude groups where every row is reviewed (or standalone row that is reviewed). */
  const visibleGroups = useMemo(() => {
    const filtered = urlState.hideReviewed
      ? groups.filter((group) => {
          const allReviewed = group.items.every(
            (item) => item.review_status === "reviewed",
          );
          return !allReviewed;
        })
      : groups;
    // Show "In Progress" groups first
    return [...filtered].sort((a, b) => {
      const aInProgress = a.items.some(
        (i) => i.review_status === "in_progress",
      );
      const bInProgress = b.items.some(
        (i) => i.review_status === "in_progress",
      );
      if (aInProgress && !bInProgress) return -1;
      if (!aInProgress && bInProgress) return 1;
      return 0;
    });
  }, [groups, urlState.hideReviewed]);

  /** All items currently shown in the table (for prefetching validation issues). */
  const visibleItems = useMemo(
    () => visibleGroups.flatMap((g) => g.items),
    [visibleGroups],
  );

  /** Fetch whether each visible item has any unresolved validation issues (one request per item, limit 1). */
  const issueQueriesResults = useQueries({
    queries: visibleItems.map((item) => ({
      queryKey: ["review", "validation-has-issues", itemKey(item)] as const,
      queryFn: async (): Promise<ValidationIssue[]> => {
        if (item.entity_type === "variant" && item.variant_id != null) {
          return getVariantIssues(String(item.variant_id), {
            status: "unresolved",
            limit: 1,
          });
        }
        return getProductIssues(String(item.product_id), {
          status: "unresolved",
          limit: 1,
        });
      },
    })),
  });

  /** Item keys that have at least one unresolved validation issue (from the prefetch above). */
  const itemKeysWithValidationIssues = useMemo(() => {
    const set = new Set<string>();
    visibleItems.forEach((item, i) => {
      const data = issueQueriesResults[i]?.data;
      if (Array.isArray(data) && data.length > 0) set.add(itemKey(item));
    });
    return set;
  }, [visibleItems, issueQueriesResults]);

  /**
   * visibleGroups further filtered by "Hide reviewed w/o issues".
   * Computed after itemKeysWithValidationIssues to avoid a circular dependency
   * (visibleItems → issueQueries → itemKeysWithValidationIssues → visibleGroups).
   * A group is hidden only when every one of its items is reviewed AND has no open issues.
   */
  const displayGroups = useMemo(() => {
    if (!urlState.hideReviewedWithoutIssues) return visibleGroups;
    return visibleGroups.filter(
      (group) =>
        !group.items.every(
          (item) =>
            item.review_status === "reviewed" &&
            !itemKeysWithValidationIssues.has(itemKey(item)),
        ),
    );
  }, [
    visibleGroups,
    urlState.hideReviewedWithoutIssues,
    itemKeysWithValidationIssues,
  ]);

  const isGroupExpanded = (key: string) =>
    !sessionState.collapsedReviewGroups.has(key);
  const toggleGroup = (key: string) => {
    setSessionState((prev) => {
      const next = new Set(prev.collapsedReviewGroups);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, collapsedReviewGroups: next };
    });
  };

  /** Item whose validation issues are expanded (resolved from queue so we have ids for the API). */
  const resolvedExpandedItem = useMemo(
    () =>
      sessionState.expandedReviewRowKey
        ? (queue.find(
            (item) => itemKey(item) === sessionState.expandedReviewRowKey,
          ) ?? null)
        : null,
    [queue, sessionState.expandedReviewRowKey],
  );
  const { data: expandedIssues = [], isLoading: expandedIssuesLoading } =
    useQuery({
      queryKey: [
        "review",
        "validation-issues",
        sessionState.expandedReviewRowKey,
        resolvedExpandedItem?.product_id,
        resolvedExpandedItem?.variant_id,
        resolvedExpandedItem?.entity_type,
      ],
      queryFn: async (): Promise<ValidationIssue[]> => {
        if (!resolvedExpandedItem) return [];
        const onlyUnresolved = { status: "unresolved" as const };
        if (
          resolvedExpandedItem.entity_type === "variant" &&
          resolvedExpandedItem.variant_id != null
        ) {
          return getVariantIssues(
            String(resolvedExpandedItem.variant_id),
            onlyUnresolved,
          );
        }
        return getProductIssues(
          String(resolvedExpandedItem.product_id),
          onlyUnresolved,
        );
      },
      enabled: !!sessionState.expandedReviewRowKey && !!resolvedExpandedItem,
    });

  const toggleReviewRowValidation = (key: string) => {
    setSessionState((prev) => ({
      ...prev,
      expandedReviewRowKey: prev.expandedReviewRowKey === key ? null : key,
    }));
  };

  useEffect(() => {
    setSelectedValidationIssueIds(new Set());
  }, [sessionState.expandedReviewRowKey]);

  const toggleValidationIssueSelection = (issueId: number) => {
    setSelectedValidationIssueIds((prev) => {
      const next = new Set(prev);
      if (next.has(issueId)) next.delete(issueId);
      else next.add(issueId);
      return next;
    });
  };

  const selectUnresolvedValidationIssues = (
    issues: ValidationIssue[],
    select: boolean,
  ) => {
    const unresolved = issues.filter((i) => i.status === "unresolved");
    setSelectedValidationIssueIds((prev) => {
      const next = new Set(prev);
      for (const issue of unresolved) {
        if (select) next.add(issue.id);
        else next.delete(issue.id);
      }
      return next;
    });
  };

  const handleGetAgentPrompt = async (item: ReviewQueueItem) => {
    const key = itemKey(item);
    setPromptLoadingKey(key);
    try {
      const group = groups.find((g) =>
        g.items.some((i) => itemKey(i) === itemKey(item)),
      );
      const useGroupReview = group != null && group.items.length > 1;
      const text = await getAgentPrompt({
        product_id: item.product_id,
        ...(item.variant_id != null && { variant_id: item.variant_id }),
        use_group_review: useGroupReview,
      });
      await navigator.clipboard.writeText(text);
      toast.success("Prompt copied to clipboard");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to fetch agent prompt",
      );
    } finally {
      setPromptLoadingKey(null);
    }
  };

  const handleScrape = async (item: ReviewQueueItem) => {
    const key = itemKey(item);
    setScrapingKey(key);
    setScrapeProgress(null);
    try {
      await scrapeProductById(item.product_id, {
        onStatus: (s) => setScrapeProgress(s),
      });
      toast.success("Scrape started for product");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to start scrape");
    } finally {
      setScrapingKey(null);
      setScrapeProgress(null);
    }
  };

  const handleStart = async (item: ReviewQueueItem) => {
    const key = itemKey(item);
    setStartingKey(key);
    const body = {
      reviewer_type: "human" as const,
      reviewer_identifier: reviewerId,
      review_type: "direct" as const,
      notes: "Started review",
    };
    try {
      if (item.entity_type === "product") {
        await markProductInProgress.mutateAsync({
          productId: item.product_id,
          body,
        });
      } else if (item.variant_id != null) {
        await markVariantInProgress.mutateAsync({
          variantId: item.variant_id,
          body,
        });
      }
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to mark as in progress",
      );
    } finally {
      setStartingKey(null);
    }
  };

  const handleCopyItemIds = async (item: ReviewQueueItem) => {
    const idsText =
      item.variant_id != null
        ? `Product ${item.product_id} · Variant ${item.variant_id}`
        : `Product ${item.product_id}`;
    try {
      await navigator.clipboard.writeText(idsText);
      toast.success("IDs copied to clipboard");
    } catch {
      toast.error("Failed to copy IDs");
    }
  };

  const handleReviewSuccess = () => {
    refetchQueue();
    refetchCounts();
  };

  const handleSourceChange = (source: string | undefined) => {
    if (source != null && source !== "") {
      handleFiltersChange({ source });
    } else {
      setUrlState((prev) => ({
        ...prev,
        source: undefined,
        page: 1,
      }));
    }
  };

  const handleMarkPageAsReviewed = async () => {
    if (queue.length === 0) return;
    setMarkingPage(true);
    const body = {
      reviewer_type: "human" as const,
      reviewer_identifier: reviewerId,
      review_type: "direct" as const,
      notes: "Bulk review: current page",
    };
    try {
      for (const item of queue) {
        if (item.entity_type === "product") {
          await reviewApi.markProductReviewed(item.product_id, body);
        } else if (item.variant_id != null) {
          await reviewApi.markVariantReviewed(item.variant_id, body);
        }
      }
      toast.success(`Marked ${queue.length} items on this page as reviewed.`);
      refetchQueue();
      refetchCounts();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to mark page as reviewed",
      );
    } finally {
      setMarkingPage(false);
    }
  };

  const selectedSet = sessionState.selectedReviewItems;
  const toggleSelection = (key: string) => {
    setSessionState((prev) => {
      const next = new Set(prev.selectedReviewItems);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, selectedReviewItems: next };
    });
  };
  const selectAllInGroup = (groupKey: string, select: boolean) => {
    const group = groups.find((g) => g.key === groupKey);
    if (!group) return;
    setSessionState((prev) => {
      const next = new Set(prev.selectedReviewItems);
      for (const item of group.items) {
        const k = itemKey(item);
        if (select) next.add(k);
        else next.delete(k);
      }
      return { ...prev, selectedReviewItems: next };
    });
  };
  const isSelected = (key: string) => selectedSet.has(key);
  const selectedCount = queue.filter((item) =>
    selectedSet.has(itemKey(item)),
  ).length;
  const groupSelectedCount = (groupKey: string) => {
    const group = groups.find((g) => g.key === groupKey);
    return group
      ? group.items.filter((item) => selectedSet.has(itemKey(item))).length
      : 0;
  };
  const isGroupFullySelected = (groupKey: string) => {
    const group = groups.find((g) => g.key === groupKey);
    if (!group) return false;
    return group.items.every((item) => selectedSet.has(itemKey(item)));
  };
  const isGroupPartiallySelected = (groupKey: string) => {
    const n = groupSelectedCount(groupKey);
    return n > 0;
  };

  const selectItemsInList = (items: ReviewQueueItem[], select: boolean) => {
    setSessionState((prev) => {
      const next = new Set(prev.selectedReviewItems);
      for (const item of items) {
        const k = itemKey(item);
        if (select) next.add(k);
        else next.delete(k);
      }
      return { ...prev, selectedReviewItems: next };
    });
  };

  const countSelectedInItems = (items: ReviewQueueItem[]) =>
    items.filter((item) => selectedSet.has(itemKey(item))).length;
  const isItemsFullySelected = (items: ReviewQueueItem[]) =>
    items.length > 0 && items.every((item) => selectedSet.has(itemKey(item)));
  const isItemsPartiallySelected = (items: ReviewQueueItem[]) => {
    const n = countSelectedInItems(items);
    return n > 0 && n < items.length;
  };
  const isPageFullySelected = isItemsFullySelected(visibleItems);
  const isPagePartiallySelected = isItemsPartiallySelected(visibleItems);

  const handleSubmitSelected = async () => {
    const selectedItems = queue.filter((item) =>
      selectedSet.has(itemKey(item)),
    );
    if (selectedItems.length === 0) return;
    setSubmittingSelected(true);
    const baseBody = {
      reviewer_type: "human" as const,
      reviewer_identifier: reviewerId,
    };
    try {
      const byGroup = new Map<string, ReviewQueueItem[]>();
      for (const item of selectedItems) {
        const key = reviewGroupKey(item);
        const list = byGroup.get(key) ?? [];
        list.push(item);
        byGroup.set(key, list);
      }
      let submitted = 0;
      for (const [, items] of byGroup) {
        const directItems = items.filter(
          (i) => i.review_status === "in_progress",
        );
        const indirectItems = items.filter(
          (i) => i.review_status !== "in_progress",
        );
        const directRefs: {
          product_id?: number;
          variant_id?: number;
          review_id: number;
        }[] = [];
        for (const item of directItems) {
          const body = {
            ...baseBody,
            review_type: "direct" as const,
            notes: "Reviewed",
          };
          const res =
            item.entity_type === "product"
              ? await reviewApi.markProductReviewed(item.product_id, body)
              : await reviewApi.markVariantReviewed(item.variant_id!, body);
          if (res.id == null) {
            throw new Error("Expected review id from mark reviewed response");
          }
          directRefs.push({
            ...(item.entity_type === "product"
              ? { product_id: item.product_id }
              : { variant_id: item.variant_id! }),
            review_id: res.id,
          });
          submitted += 1;
        }
        const indirectNote =
          directRefs.length > 0
            ? "Indirectly reviewed by: " +
              directRefs
                .map((r) => {
                  if (r.product_id != null)
                    return `product_id ${r.product_id} (review_id ${r.review_id})`;
                  return `variant_id ${r.variant_id} (review_id ${r.review_id})`;
                })
                .join("; ")
            : "Indirectly verified (batch submit, no direct reviews in this group).";
        for (const item of indirectItems) {
          const body = {
            ...baseBody,
            review_type: "indirect" as const,
            notes: indirectNote,
          };
          if (item.entity_type === "product") {
            await reviewApi.markProductReviewed(item.product_id, body);
          } else {
            await reviewApi.markVariantReviewed(item.variant_id!, body);
          }
          submitted += 1;
        }
      }
      setSessionState((prev) => {
        const next = new Set(prev.selectedReviewItems);
        for (const item of selectedItems) next.delete(itemKey(item));
        return { ...prev, selectedReviewItems: next };
      });
      toast.success(`Submitted ${submitted} review(s).`);
      refetchQueue();
      refetchCounts();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to submit selected reviews",
      );
    } finally {
      setSubmittingSelected(false);
    }
  };

  const handleGetBatchedAgentPrompt = async () => {
    const selectedItems = queue.filter((item) =>
      selectedSet.has(itemKey(item)),
    );
    const variantItems = selectedItems.filter(
      (item) => item.variant_id != null,
    );
    if (variantItems.length === 0) return;

    const productIds = new Set(variantItems.map((i) => i.product_id));
    if (productIds.size > 1) {
      toast.error("All selected variants must belong to the same product");
      return;
    }

    const productId = variantItems[0]!.product_id;
    const variantIdList = variantItems.map((i) => i.variant_id!);
    setBatchPromptLoading(true);
    try {
      const text = await getBatchedAgentPrompt({
        product_id: productId,
        variant_ids: variantIdList,
        use_group_review: true,
      });
      await navigator.clipboard.writeText(text);
      toast.success(`Batched prompt copied (${variantIdList.length} variants)`);
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to generate batch prompt",
      );
    } finally {
      setBatchPromptLoading(false);
    }
  };

  const totalNeedingReview =
    counts?.totals.unreviewed != null && counts?.totals.stale != null
      ? counts.totals.unreviewed + counts.totals.stale
      : 0;

  return (
    <div className="space-y-6">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">Reviewing</h2>
        <Button variant="outline" size="sm" onClick={handleRefresh}>
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Counts / dashboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" />
            Review status
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Stats for:{" "}
            <span className="font-medium text-foreground">
              {urlState.source ?? "All sources"}
            </span>
          </p>
        </CardHeader>
        <CardContent>
          {countsLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading counts…
            </div>
          ) : counts ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">
                  Items needing review
                </p>
                <p className="text-2xl font-semibold">{totalNeedingReview}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="text-amber-600 dark:text-amber-400">
                    Stale: {counts.totals.stale}
                  </span>
                  {" · "}
                  <span className="text-zinc-500">
                    Unreviewed: {counts.totals.unreviewed}
                  </span>
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Products</p>
                <p className="text-2xl font-semibold">
                  {counts.products.unreviewed + counts.products.stale} pending
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {counts.products.reviewed} reviewed
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Variants</p>
                <p className="text-2xl font-semibold">
                  {counts.variants.unreviewed + counts.variants.stale} pending
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {counts.variants.reviewed} reviewed
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">
                  Reviewed (total)
                </p>
                <p className="text-2xl font-semibold text-[#10B981]">
                  {counts.totals.reviewed}
                </p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Open issues</p>
                {issueCountLoading ? (
                  <p className="text-2xl font-semibold text-muted-foreground">
                    …
                  </p>
                ) : (
                  <p className="text-2xl font-semibold text-orange-500">
                    {issueCount?.count ?? 0}
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Queue filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Status
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal"
                  >
                    <span className="truncate">
                      {selectedStatuses.size === 0 ||
                      selectedStatuses.size === REVIEW_STATUS_OPTIONS.length
                        ? "All"
                        : REVIEW_STATUS_OPTIONS.filter((o) =>
                            selectedStatuses.has(o.value),
                          )
                            .map((o) => o.label)
                            .join(", ")}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <div className="flex flex-col gap-1">
                    {REVIEW_STATUS_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
                      >
                        <Checkbox
                          checked={selectedStatuses.has(opt.value)}
                          onCheckedChange={(checked) => {
                            setUrlState((prev) => {
                              const next = new Set(selectedStatuses);
                              if (checked) next.add(opt.value);
                              else next.delete(opt.value);
                              const nextStatus =
                                next.size === 0 ||
                                next.size === REVIEW_STATUS_OPTIONS.length
                                  ? undefined
                                  : Array.from(next).join(",");
                              return {
                                ...prev,
                                page: 1,
                                status: nextStatus,
                              };
                            });
                          }}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Source
              </label>
              <SourceCombobox
                sources={sources}
                isLoading={sourcesLoading}
                value={urlState.source}
                onValueChange={handleSourceChange}
                placeholder="All sources"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Filter by scraper source or bulk review a source.
              </p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Entity type
              </label>
              <Select
                value={urlState.entity_type ?? "all"}
                onValueChange={(v) => {
                  if (v === "all") {
                    setUrlState((prev) => ({
                      ...prev,
                      entity_type: undefined,
                      page: 1,
                    }));
                  } else {
                    handleFiltersChange({ entity_type: v });
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Products & variants</SelectItem>
                  <SelectItem value="product">Products only</SelectItem>
                  <SelectItem value="variant">Variants only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">
                Product ID
              </label>
              <Input
                type="number"
                min={1}
                placeholder="All products"
                value={
                  urlState.product_id !== undefined &&
                  urlState.product_id !== null
                    ? urlState.product_id
                    : ""
                }
                onChange={(e) => {
                  const v = e.target.value.trim();
                  if (v === "") {
                    setUrlState((prev) => ({
                      ...prev,
                      product_id: undefined,
                      page: 1,
                    }));
                  } else {
                    const num = parseInt(v, 10);
                    if (!Number.isNaN(num) && num >= 1) {
                      handleFiltersChange({ product_id: num });
                    }
                  }
                }}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Filter queue to a single product (e.g. when viewing variants).
              </p>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <Button
                variant={urlState.hideReviewed ? "default" : "outline"}
                size="sm"
                onClick={() =>
                  setUrlState((prev) => ({
                    ...prev,
                    hideReviewed: !prev.hideReviewed,
                  }))
                }
                title="Hide groups where every row is reviewed (or standalone reviewed rows)"
              >
                {urlState.hideReviewed ? (
                  <Eye className="w-3.5 h-3.5 mr-2" />
                ) : (
                  <EyeOff className="w-3.5 h-3.5 mr-2" />
                )}
                {urlState.hideReviewed
                  ? "Showing only needing review"
                  : "Hide reviewed"}
              </Button>
              <Button
                variant={urlState.hideCanceled ? "default" : "outline"}
                size="sm"
                onClick={() =>
                  setUrlState((prev) => ({
                    ...prev,
                    hideCanceled: !prev.hideCanceled,
                    page: 1,
                  }))
                }
                title="Hide canceled reviews from the queue"
              >
                {urlState.hideCanceled ? (
                  <Eye className="w-3.5 h-3.5 mr-2" />
                ) : (
                  <EyeOff className="w-3.5 h-3.5 mr-2" />
                )}
                {urlState.hideCanceled
                  ? "Showing without canceled"
                  : "Hide canceled"}
              </Button>
              <Button
                variant={
                  urlState.hideReviewedWithoutIssues ? "default" : "outline"
                }
                size="sm"
                onClick={() =>
                  setUrlState((prev) => ({
                    ...prev,
                    hideReviewedWithoutIssues: !prev.hideReviewedWithoutIssues,
                  }))
                }
                title="Hide reviewed items/groups that have no open validation issues"
              >
                {urlState.hideReviewedWithoutIssues ? (
                  <Eye className="w-3.5 h-3.5 mr-2" />
                ) : (
                  <EyeOff className="w-3.5 h-3.5 mr-2" />
                )}
                {urlState.hideReviewedWithoutIssues
                  ? "Showing reviewed with issues"
                  : "Hide reviewed w/o issues"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk review by source */}
      {urlState.source && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Bulk review source: {urlState.source}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Mark all items for this source as reviewed in one go (e.g. after
              verifying the source elsewhere).
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() =>
                  setSessionState({ ...sessionState, bulkDialogOpen: true })
                }
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Mark entire source as reviewed
              </Button>
              <Button
                variant="outline"
                onClick={handleMarkPageAsReviewed}
                disabled={queue.length === 0 || markingPage}
              >
                {markingPage ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : null}
                Mark this page ({queue.length} items) as reviewed
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Queue list */}
      <Card>
        <CardHeader className="pb-0">
          <div className="pb-4 flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle>Review queue</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {viewMode === "matrix"
                  ? "Product × Grade matrix view. Review the planned diagonal cells then resolve to propagate indirect coverage."
                  : variantQueueByProduct
                    ? "Variants are grouped by product; within each product, consecutive rows with the same grade stay in a subgroup (same as the default queue). Use the product checkbox for all variants, or the subgroup checkbox for that grade only. Pagination stays aligned with the API. STALE items are higher priority. Submit marks in-progress rows as direct and the rest as indirect."
                    : "Rows use server-side grouping (product or variants with same grade); pagination keeps groups intact. STALE items are higher priority. Select items and submit to mark in-progress as direct and the rest as indirect."}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="sm"
                variant={viewMode === "variants" ? "default" : "outline"}
                onClick={() =>
                  setUrlState((prev) => ({ ...prev, view_mode: "variants" }))
                }
                title="Variant groups view"
              >
                <List className="w-4 h-4 mr-1.5" />
                Variant groups
              </Button>
              <Button
                size="sm"
                variant={viewMode === "matrix" ? "default" : "outline"}
                onClick={() =>
                  setUrlState((prev) => ({ ...prev, view_mode: "matrix" }))
                }
                title="Product × Grade matrix view (requires a source)"
              >
                <LayoutGrid className="w-4 h-4 mr-1.5" />
                Matrix
              </Button>
            </div>
          </div>
        </CardHeader>
        {viewMode === "variants" && (
          <div className="sticky top-0 z-10 border-t border-b border-border bg-card px-6 py-3">
            <div className="flex flex-wrap items-center justify-start gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={
                    isPageFullySelected
                      ? true
                      : isPagePartiallySelected
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(checked) =>
                    selectItemsInList(visibleItems, checked === true)
                  }
                  aria-label="Select all rows on this page"
                />
                Select all on page
              </label>
              {selectedCount > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setSessionState((prev) => ({
                        ...prev,
                        selectedReviewItems: new Set<string>(),
                      }))
                    }
                  >
                    Clear selection
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGetBatchedAgentPrompt}
                    disabled={batchPromptLoading || selectedCount === 0}
                    title="Generate and copy a self-contained batched prompt for all selected variants"
                  >
                    {batchPromptLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ClipboardCheck className="w-4 h-4 mr-2" />
                    )}
                    Get batch prompt ({selectedCount})
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSubmitSelected}
                    disabled={submittingSelected}
                  >
                    {submittingSelected ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Submit selected ({selectedCount})
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        <CardContent>
          {viewMode === "matrix" ? (
            urlState.source ? (
              <ReviewMatrix
                source={urlState.source}
                reviewerId={reviewerId}
                onChanged={handleReviewSuccess}
                onCellClick={(cell) =>
                  setUrlState((prev) => ({
                    ...prev,
                    view_mode: "variants",
                    entity_type: "variant",
                    product_id: cell.product_id,
                    page: 1,
                  }))
                }
              />
            ) : (
              <p className="text-center py-12 text-muted-foreground">
                Select a source to use the matrix view.
              </p>
            )
          ) : queueLoading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : queueForDisplay.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">
              No items in the queue for the current filters.
            </p>
          ) : (
            <>
              <ul className="space-y-2">
                {displayGroups.map((group) => {
                  const expanded = isGroupExpanded(group.key);
                  const isSingle = group.items.length === 1;
                  const singleItem = isSingle ? group.items[0] : null;

                  const renderRow = (item: ReviewQueueItem) => {
                    const key = itemKey(item);
                    const isValidationExpanded =
                      sessionState.expandedReviewRowKey === key;
                    const isExpandedRow =
                      sessionState.expandedReviewRowKey === key;
                    const thisRowIssues = isExpandedRow ? expandedIssues : [];
                    const thisRowLoading =
                      isExpandedRow && expandedIssuesLoading;
                    const unresolvedIssuesForRow = thisRowIssues.filter(
                      (i) => i.status === "unresolved",
                    );
                    const unresolvedIdsForRow = new Set(
                      unresolvedIssuesForRow.map((i) => i.id),
                    );
                    const selectedUnresolvedCount = Array.from(
                      selectedValidationIssueIds,
                    ).filter((id) => unresolvedIdsForRow.has(id)).length;
                    const allUnresolvedSelected =
                      unresolvedIssuesForRow.length > 0 &&
                      selectedUnresolvedCount === unresolvedIssuesForRow.length;
                    const someUnresolvedSelected =
                      selectedUnresolvedCount > 0 && !allUnresolvedSelected;
                    const selectedUnresolvedIssues =
                      unresolvedIssuesForRow.filter((i) =>
                        selectedValidationIssueIds.has(i.id),
                      );
                    const hasValidationIssues =
                      (isExpandedRow && expandedIssues.length > 0) ||
                      itemKeysWithValidationIssues.has(key);
                    return (
                      <li
                        key={key}
                        className={cn(
                          "rounded-lg border overflow-hidden",
                          hasValidationIssues && "border-orange-300 border-2",
                        )}
                      >
                        <div className="flex flex-wrap items-center gap-3 p-4 hover:bg-muted/50 transition-colors">
                          <Checkbox
                            checked={isSelected(key)}
                            onCheckedChange={() => toggleSelection(key)}
                            aria-label={`Select ${item.product_name} for batch submit`}
                            className="shrink-0"
                          />
                          <button
                            type="button"
                            onClick={() => toggleReviewRowValidation(key)}
                            className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted"
                            title={
                              isValidationExpanded
                                ? "Hide validation issues"
                                : "Show validation issues"
                            }
                            aria-expanded={isValidationExpanded}
                          >
                            {isValidationExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                          <Badge variant="outline" className="shrink-0">
                            {item.entity_type}
                          </Badge>
                          <StatusBadge status={item.review_status} />
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">
                              {item.product_name}
                            </p>
                            {item.variant_name && (
                              <p className="text-sm text-muted-foreground break-words">
                                Variant: {item.variant_name}
                              </p>
                            )}
                            {item.grade_name != null &&
                              item.grade_name !== "" && (
                                <p className="text-sm text-muted-foreground truncate">
                                  Grade: {item.grade_name}
                                </p>
                              )}
                            <div className="mt-0.5 flex items-center gap-1">
                              <p className="text-xs text-muted-foreground">
                                Product {item.product_id}
                                {item.variant_id != null
                                  ? ` · Variant ${item.variant_id}`
                                  : ""}
                              </p>
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="h-5 w-5"
                                onClick={() => handleCopyItemIds(item)}
                                title="Copy product and variant IDs"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {item.product_url && (
                                <a
                                  href={item.product_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:underline inline-flex items-center gap-0.5"
                                >
                                  Product <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                              {item.variant_url && (
                                <a
                                  href={item.variant_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:underline inline-flex items-center gap-0.5"
                                >
                                  Variant <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                          <span className="text-sm text-muted-foreground shrink-0">
                            {item.source}
                          </span>
                          <span className="text-sm text-muted-foreground shrink-0">
                            {item.last_reviewed_at
                              ? formatRelativeTime(item.last_reviewed_at)
                              : "Never"}
                          </span>
                          <div className="flex gap-2 shrink-0 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleScrape(item)}
                              disabled={scrapingKey === itemKey(item)}
                              title="Scrape product page URL"
                            >
                              {scrapingKey === itemKey(item) ? (
                                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              ) : null}
                              {scrapingKey === itemKey(item) && scrapeProgress
                                ? scrapeProgress === "in_progress"
                                  ? "Scrape in progress…"
                                  : "Queued…"
                                : "Scrape"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleGetAgentPrompt(item)}
                              title="Copy agent prompt to clipboard"
                              disabled={promptLoadingKey === itemKey(item)}
                            >
                              {promptLoadingKey === itemKey(item) ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                "Prompt"
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              asChild
                              title="View data and rendered HTML on Product Viewer"
                            >
                              <Link
                                to={{
                                  pathname: "/product-viewer",
                                  search:
                                    item.entity_type === "product"
                                      ? `?type=product&id=${item.product_id}`
                                      : `?type=variant&id=${item.variant_id ?? item.product_id}`,
                                }}
                              >
                                <Eye className="w-3.5 h-3.5 mr-1" />
                                Viewer
                              </Link>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStart(item)}
                              disabled={startingKey === itemKey(item)}
                            >
                              {startingKey === itemKey(item) ? (
                                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                              ) : (
                                <Play className="w-3.5 h-3.5 mr-1" />
                              )}
                              Start
                            </Button>
                            {item.review_status === "stale" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReview(item)}
                                title="View diff that made this stale"
                              >
                                <FileDiff className="w-3.5 h-3.5 mr-1" />
                                View changes
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleReview(item)}
                            >
                              Review
                            </Button>
                          </div>
                        </div>
                        {isValidationExpanded && (
                          <div className="border-t bg-muted/20 px-4 py-3">
                            {thisRowLoading ? (
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Loading validation issues…
                              </div>
                            ) : thisRowIssues.length === 0 ? (
                              <p className="text-sm text-muted-foreground">
                                No validation issues
                              </p>
                            ) : (
                              <>
                                {unresolvedIssuesForRow.length > 0 && (
                                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                    <label className="inline-flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                                      <Checkbox
                                        checked={
                                          allUnresolvedSelected
                                            ? true
                                            : someUnresolvedSelected
                                              ? "indeterminate"
                                              : false
                                        }
                                        onCheckedChange={(checked) =>
                                          selectUnresolvedValidationIssues(
                                            thisRowIssues,
                                            checked === true,
                                          )
                                        }
                                        aria-label="Select all unresolved issues for this row"
                                      />
                                      Select all unresolved (
                                      {unresolvedIssuesForRow.length})
                                    </label>
                                    {selectedUnresolvedCount > 0 && (
                                      <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() =>
                                            setSelectedValidationIssueIds(
                                              new Set(),
                                            )
                                          }
                                        >
                                          Clear selection
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="secondary"
                                          onClick={() =>
                                            setBulkResolveIssues(
                                              selectedUnresolvedIssues,
                                            )
                                          }
                                          title="Apply one resolution type and optional notes to the selected issues"
                                        >
                                          <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                                          Resolve selected (
                                          {selectedUnresolvedCount})
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <ul className="space-y-2">
                                  {thisRowIssues.map((issue) => (
                                    <li
                                      key={issue.id}
                                      className={cn(
                                        "flex items-start gap-2 rounded border px-3 py-2 text-sm",
                                        issue.level === "error"
                                          ? "border-destructive/50 bg-destructive/5"
                                          : "border-amber-500/30 bg-amber-500/5",
                                      )}
                                    >
                                      {issue.status === "unresolved" ? (
                                        <Checkbox
                                          checked={selectedValidationIssueIds.has(
                                            issue.id,
                                          )}
                                          onCheckedChange={() =>
                                            toggleValidationIssueSelection(
                                              issue.id,
                                            )
                                          }
                                          aria-label={`Select issue: ${issue.message}`}
                                          className="shrink-0 mt-0.5"
                                        />
                                      ) : (
                                        <span className="w-4 shrink-0" />
                                      )}
                                      {issue.level === "error" ? (
                                        <AlertCircle className="w-4 h-4 shrink-0 text-destructive mt-0.5" />
                                      ) : (
                                        <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
                                      )}
                                      <div className="min-w-0 flex-1">
                                        <span className="font-medium uppercase text-xs text-muted-foreground">
                                          {issue.level}
                                        </span>
                                        {issue.validation_type && (
                                          <span className="ml-2 text-xs text-muted-foreground">
                                            {issue.validation_type}
                                          </span>
                                        )}
                                        <p className="mt-0.5">
                                          {issue.message}
                                        </p>
                                        {(issue.field_name ?? issue.status) && (
                                          <p className="text-xs text-muted-foreground mt-1">
                                            {[
                                              issue.field_name,
                                              issue.status === "addressed"
                                                ? "Resolved"
                                                : "Open",
                                            ]
                                              .filter(Boolean)
                                              .join(" · ")}
                                          </p>
                                        )}
                                      </div>
                                      {issue.status === "unresolved" && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="shrink-0"
                                          onClick={() => setResolveIssue(issue)}
                                          title="Resolve this validation issue"
                                        >
                                          <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                          Resolve
                                        </Button>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  };

                  if (isSingle && singleItem && !variantQueueByProduct) {
                    return (
                      <Fragment key={group.key}>
                        {renderRow(singleItem)}
                      </Fragment>
                    );
                  }

                  const groupHasValidationIssues = group.items.some((item) =>
                    itemKeysWithValidationIssues.has(itemKey(item)),
                  );
                  return (
                    <li
                      key={group.key}
                      className={cn(
                        "rounded-lg border-2 overflow-hidden",
                        groupHasValidationIssues
                          ? "border-orange-300"
                          : "border-green-500",
                      )}
                    >
                      <div className="flex flex-wrap items-center gap-3 p-4 hover:bg-muted/50 transition-colors">
                        <Checkbox
                          checked={
                            isGroupFullySelected(group.key)
                              ? true
                              : isGroupPartiallySelected(group.key)
                                ? "indeterminate"
                                : false
                          }
                          onCheckedChange={(checked) =>
                            selectAllInGroup(group.key, checked === true)
                          }
                          onClick={(e) => e.stopPropagation()}
                          aria-label={
                            variantQueueByProduct
                              ? `Select all ${group.items.length} variants for product ${group.items[0]?.product_id ?? ""}`
                              : `Select all ${group.items.length} items in group`
                          }
                          className="shrink-0"
                        />
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.key)}
                          className="flex flex-wrap items-center gap-3 text-left flex-1 min-w-0"
                        >
                          {expanded ? (
                            <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
                          )}
                          <Layers className="w-4 h-4 shrink-0 text-muted-foreground" />
                          <span className="font-medium text-sm">
                            {group.label}
                          </span>
                          <Badge variant="secondary" className="shrink-0">
                            {group.items.length} items
                          </Badge>
                        </button>
                      </div>
                      {expanded && (
                        <div className="border-t bg-muted/20 p-2 space-y-2">
                          {variantQueueByProduct &&
                          group.subgroups &&
                          variantSubgroupsNeedChrome(group.subgroups) ? (
                            group.subgroups.map((sg) => (
                              <div
                                key={sg.key}
                                className="rounded-md border bg-background/50 overflow-hidden"
                              >
                                <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-muted/30 border-b">
                                  <Checkbox
                                    checked={
                                      isItemsFullySelected(sg.items)
                                        ? true
                                        : isItemsPartiallySelected(sg.items)
                                          ? "indeterminate"
                                          : false
                                    }
                                    onCheckedChange={(checked) =>
                                      selectItemsInList(
                                        sg.items,
                                        checked === true,
                                      )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label={`Select all in subgroup: ${sg.label}`}
                                    className="shrink-0"
                                  />
                                  <span className="text-sm font-medium">
                                    {sg.label}
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className="shrink-0 text-xs"
                                  >
                                    {sg.items.length}
                                  </Badge>
                                </div>
                                <ul className="space-y-2 p-2">
                                  {sg.items.map((item) => renderRow(item))}
                                </ul>
                              </div>
                            ))
                          ) : (
                            <ul className="space-y-2">
                              {group.items.map((item) => renderRow(item))}
                            </ul>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Page {urlState.page || 1}
                  {queue.length === QUEUE_PAGE_SIZE && " (more available)"}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(urlState.page || 1) <= 1}
                    onClick={() => handlePageChange((urlState.page || 1) - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={queue.length < QUEUE_PAGE_SIZE}
                    onClick={() => handlePageChange((urlState.page || 1) + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ReviewItemDialog
        item={selectedItem}
        open={sessionState.reviewDialogOpen}
        onOpenChange={(open) =>
          setSessionState({ ...sessionState, reviewDialogOpen: open })
        }
        onSuccess={handleReviewSuccess}
      />

      {urlState.source && (
        <BulkReviewDialog
          source={urlState.source}
          open={sessionState.bulkDialogOpen}
          onOpenChange={(open) =>
            setSessionState({ ...sessionState, bulkDialogOpen: open })
          }
          onSuccess={handleReviewSuccess}
        />
      )}

      <ResolveIssueDialog
        issue={resolveIssue}
        open={resolveIssue != null}
        onOpenChange={(open) => !open && setResolveIssue(null)}
        onSuccess={() =>
          queryClient.invalidateQueries({ queryKey: ["review"] })
        }
      />

      <IssuesBulkResolveDialog
        issues={bulkResolveIssues ?? []}
        open={bulkResolveIssues != null && bulkResolveIssues.length > 0}
        onOpenChange={(open) => !open && setBulkResolveIssues(null)}
        onResolved={() => {
          queryClient.invalidateQueries({ queryKey: ["review"] });
          setSelectedValidationIssueIds(new Set());
        }}
        title="Resolve selected warnings"
        description="Apply the same resolution type and optional notes to each selected unresolved warning for this product or variant."
      />
    </div>
  );
}
